// Anita Week 3 / Day 3 — Force, Push, Pull and Magnets.
// Content is supplied verbatim by the curriculum author; this file only places
// that content into the existing Nimny Lab lesson schema. No pedagogy is invented.
// SAFETY: only a LARGE safe magnet under direct parent supervision. NO small magnets,
// never put a magnet in the mouth, never bring a magnet near phones, computers,
// bank cards, or medical devices.

const day3 = {
  id: 'day-3',
  studentId: 'anita',
  weekId: 'week-3',
  dayId: 'day-3',
  status: 'active',
  title: 'Сила, толчок, тяга и магниты / Force, Push, Pull and Magnets',
  shortTitle: 'Сила и магниты / Force and Magnets',
  theme:
    'Анита учится понимать, что сила может толкать, тянуть или притягивать предметы. / Anita learns that force can push, pull, or attract objects.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'science', 'force', 'magnets', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни нашла большой безопасный магнит и коробку предметов. Некоторые предметы притягиваются, а некоторые нет. Сегодня Анита узнает, что сила может толкать, тянуть и притягивать. / Nimny finds a large safe magnet and a box of objects. Some objects are attracted and some are not. Today Anita learns that force can push, pull and attract.',
  goals: [
    'Понять слова сила, толкать, тянуть, магнит. / Understand force, push, pull, magnet.',
    'Отличать «притягивает» и «не притягивает». / Tell attracted from not attracted.',
    'Сортировать предметы по результату опыта. / Sort objects by experiment result.',
    'Решать задачи на подсчёт и сравнение. / Count and compare.',
    'Сделать безопасную лабораторную карточку. / Make a safe lab card.'
  ],
  vocabulary: [
    { term: 'сила / force', definition: 'то, что может изменить движение предмета. / something that can change the motion of an object.' },
    { term: 'толкать / push', definition: 'двигать от себя. / move away from yourself.' },
    { term: 'тянуть / pull', definition: 'двигать к себе. / move toward yourself.' },
    { term: 'магнит / magnet', definition: 'предмет, который притягивает некоторые металлы. / an object that attracts some metals.' },
    { term: 'притягивает / attracts', definition: 'тянет к себе. / pulls toward itself.' },
    { term: 'не притягивает / does not attract', definition: 'не тянет к себе. / does not pull toward itself.' },
    { term: 'металл / metal', definition: 'материал, который иногда притягивается магнитом. / a material sometimes attracted by a magnet.' },
    { term: 'опыт / experiment', definition: 'проверка, чтобы узнать результат. / a test to find out the result.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Слова силы / Force Words',
      prompt: 'Прочитай по-русски:\nсила\nтолкать\nтянуть\nмагнит\nпритягивает\nне притягивает\nметалл\nопыт',
      ruPrimaryText: 'Прочитай 8 русских слов силы и магнитов.',
      enHelpText: 'Read the Russian force words first. English is only for help.',
      hint: 'Слова «притягивает» и «не притягивает» читай медленно. / Read longer words slowly.',
      answer: 'Анита пробует читать русские слова. / Anita attempts the Russian reading.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Маленькое чтение / Short Reading',
      prompt: 'Прочитай по-русски:\nСила может толкать.\nСила может тянуть.\nМагнит притягивает некоторые предметы.',
      ruPrimaryText: 'Прочитай 3 короткие фразы по-русски.',
      enHelpText: 'Force can push. Force can pull. A magnet attracts some objects.',
      hint: 'Сначала попробуй сама. / Try it first.',
      answer: 'Анита пробует читать 3 фразы. / Anita attempts the short reading.'
    },
    {
      id: 'math-magnet-sort',
      type: 'text',
      title: 'Считаем результаты / Count the Results',
      prompt:
        'Нимни проверила 10 предметов.\n4 предмета притягиваются магнитом.\n6 предметов не притягиваются.\n\nОтветь:\n1. Сколько предметов всего?\n2. На сколько больше предметов не притягиваются?\n3. Если добавить ещё 2 предмета, которые притягиваются, сколько будет притягиваться?',
      ruPrimaryText: 'Посчитай результаты опыта и ответь на 3 вопроса.',
      enHelpText: '10 items: 4 attracted, 6 not attracted. Answer 3 questions.',
      answer: '1) 4 + 6 = 10. 2) 6 − 4 = 2. 3) 4 + 2 = 6.'
    },
    {
      id: 'magnet-prediction',
      type: 'text',
      title: 'Предскажи результат / Predict the Result',
      prompt:
        'Перед опытом учёный делает предсказание.\nВыбери, что может притянуться к магниту.\n\nПредметы:\n1. скрепка\n2. деревянный карандаш\n3. пластиковая ложка\n4. металлическая крышка\n5. бумага\n6. монета\n\nОтвет:\nКакие предметы, скорее всего, притянутся?',
      ruPrimaryText: 'Сделай предсказание: какие предметы из списка могут притянуться?',
      enHelpText: 'Predict which items may be attracted to a magnet. Note: coin result depends on the metal.',
      hint: 'Предсказание — это не оценка, это твоё ожидание перед опытом. / A prediction is your expectation before the test.',
      answer: 'Скорее всего: скрепка, металлическая крышка. Монета зависит от металла. Карандаш, пластиковая ложка и бумага обычно не притягиваются.'
    },
    {
      id: 'science-magnet-test',
      type: 'text',
      title: 'Безопасный опыт с магнитом / Safe Magnet Test',
      prompt:
        'С родителем используй только большой безопасный магнит.\nНе использовать маленькие магниты.\nНе класть магнит в рот.\nНе подносить магнит к телефону, банковским картам или компьютеру.\n\nПроверь 3 безопасных предмета.\nЗапиши:\nПредмет 1: притягивается / не притягивается.\nПредмет 2: притягивается / не притягивается.\nПредмет 3: притягивается / не притягивается.',
      ruPrimaryText: 'С родителем безопасно проверь 3 предмета большим магнитом и запиши результат.',
      enHelpText: 'With a parent, use ONLY a large safe magnet. No small magnets. Never put the magnet in the mouth. Keep it away from phones, computers, bank cards. Test 3 safe items.',
      hint: 'Только большой безопасный магнит и только с родителем. / Large safe magnet with a parent only.',
      answer: 'Open-ended. Parent checks safety and results.'
    },
    {
      id: 'data-magnet-chart',
      type: 'text',
      title: 'Мини-таблица / Mini Chart',
      prompt:
        'В опыте получилось:\nпритягивается — 3\nне притягивается — 5\n\nОтветь:\n1. Сколько предметов всего?\n2. На сколько «не притягивается» больше?\n3. Если ещё 1 предмет притянулся, сколько будет «притягивается»?',
      ruPrimaryText: 'Заполни мини-таблицу и ответь на 3 вопроса.',
      enHelpText: 'Attracted = 3, not attracted = 5. Answer 3 questions.',
      answer: '1) 3 + 5 = 8. 2) 5 − 3 = 2. 3) 3 + 1 = 4.'
    },
    {
      id: 'spelling-force',
      type: 'text',
      title: 'Правописание / Spelling',
      prompt: 'Напиши:\nсила\nмагнит\nметалл\nопыт\n\nВставь буквы:\nс_ла\nмагн_т\nмета_л\nоп_т',
      ruPrimaryText: 'Напиши 4 слова и вставь пропущенные буквы.',
      enHelpText: 'Write 4 words: force, magnet, metal, experiment. Then fill the missing letters.',
      answer: 'сила, магнит, металл, опыт.'
    },
    {
      id: 'creative-magnet-card',
      type: 'text',
      title: 'Карточка опыта / Experiment Card',
      prompt:
        'Сделай карточку опыта.\nНарисуй магнит и 3 предмета.\nПодпиши:\nПритягивается: ______.\nНе притягивается: ______.\nМой вывод: магнит притягивает ______.',
      ruPrimaryText: 'Нарисуй карточку опыта: магнит, 3 предмета и один вывод.',
      enHelpText: 'Draw an experiment card: magnet, 3 items, labels and one conclusion.',
      answer: 'Open-ended. Accept if Anita draws the experiment and makes one conclusion.'
    },
    {
      id: 'speech-magnet-explain',
      type: 'text',
      title: 'Объясни родителю / Explain to Parent',
      prompt: 'Скажи родителю по-русски:\nЯ сделала опыт.\nМагнит притягивает ______.\nМагнит не притягивает ______.',
      ruPrimaryText: 'Объясни родителю по-русски результат опыта.',
      enHelpText: 'Tell a parent in Russian: I did the experiment. The magnet attracts ___. The magnet does not attract ___.',
      answer: 'Accept if Anita explains at least one attracted and one not attracted item.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши слова дня.',
    '2. Прочитай 3 фразы.',
    '3. Реши math-задачи.',
    '4. С родителем проверь 3 безопасных предмета большим магнитом.',
    '5. Нарисуй магнит и предметы.',
    '6. Напиши один вывод.',
    '7. Объясни родителю результат.'
  ],
  printWorksheet: {
    title: 'Сила и магниты / Force and Magnets',
    subtitle: 'Неделя 3 · День 3 / Week 3 · Day 3',
    pages: [
      {
        title: 'Маршрут дня / Route',
        blocks: [
          { type: 'text', text: 'Миссия: Нимни изучает силу и магниты. / Nimny explores force and magnets.' },
          { type: 'list', items: [
            'Слова дня (8 слов). / Words of the day (8 words).',
            'Маленькое чтение: 3 фразы. / Short reading: 3 sentences.',
            'English help мелким шрифтом — только если трудно. / English help in small print — only if hard.'
          ] }
        ]
      },
      {
        title: 'Математика и предсказание / Math and Prediction',
        blocks: [
          { type: 'text', text: 'Считаем результаты и делаем предсказание. / Count results and make a prediction.' },
          { type: 'list', items: [
            '10 предметов: 4 притягиваются, 6 не притягиваются. / 10 items.',
            'Предсказание: скрепка, карандаш, ложка, крышка, бумага, монета. / Prediction list.',
            'Мини-таблица: 3 и 5. / Mini chart: 3 and 5.'
          ] }
        ]
      },
      {
        title: 'Безопасный опыт / Safe Experiment',
        blocks: [
          { type: 'text', text: 'Карточка опыта и вывод. / Experiment card and conclusion.' },
          { type: 'list', items: [
            'Правописание: сила, магнит, металл, опыт. / Spelling.',
            'С родителем проверить 3 безопасных предмета большим магнитом. / Test 3 safe items with a parent and a large magnet only.',
            'Нарисовать карточку опыта (магнит и 3 предмета). / Draw the experiment card.',
            'Написать один вывод. / Write one conclusion.',
            'Parent Check.',
            'Notebook Mode fallback — если нет принтера, всё то же в тетради. / Notebook Mode fallback.'
          ] }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита понимает, что сила может толкать, тянуть и притягивать, а магнит притягивает не все предметы.',
    mathGoal: 'Сложение, вычитание, сравнение, простая таблица результатов.',
    russianGoal: 'Прочитать слова сила, толкать, тянуть, магнит, притягивает, не притягивает, металл, опыт.',
    readingWritingGoal: 'Прочитать 3 фразы, написать 4 слова, подписать карточку опыта.',
    practicalGoal: 'Сделать безопасный опыт с большим магнитом и 3 предметами под присмотром родителя.',
    parentTheoryCard: {
      title: 'Как объяснить силу и магниты ребёнку',
      purpose: 'Помочь родителю объяснить силу как действие: толкать, тянуть или притягивать.',
      keyMessage: 'Сила может изменить движение предмета. Магнит — это пример силы, которая притягивает некоторые предметы без прикосновения.',
      parentScript: [
        '«Толкать — это двигать от себя.»',
        '«Тянуть — это двигать к себе.»',
        '«Магнит может тянуть некоторые предметы к себе.»',
        '«Но магнит притягивает не всё. Поэтому мы проверяем.»'
      ],
      practiceSteps: [
        'Показать push: мягко толкнуть коробочку.',
        'Показать pull: мягко потянуть коробочку.',
        'С родителем проверить 3 безопасных предмета большим магнитом.',
        'Сравнить предсказание и результат.',
        'Сделать вывод.'
      ],
      doNotSay: [
        'Не говорить, что магнит притягивает всё металлическое всегда.',
        'Не использовать маленькие магниты.',
        'Не давать магнит как игрушку без присмотра.',
        'Не делать опыт рядом с телефоном, компьютером или банковскими картами.'
      ],
      paperOnlyNotes: [
        'Можно рисовать опыт в тетради.',
        'Использовать только большой безопасный магнит.',
        'Не использовать мелкие магниты, которые можно проглотить.'
      ]
    },
    whatThisLessonTeaches: 'Анита учится делать предсказание, проверять результат и делать вывод.',
    keyMessage: 'Я могу предсказать, проверить и сделать вывод: магнит притягивает некоторые предметы, но не все.',
    howToExplain: 'Скажите: «Сначала мы думаем, что может произойти. Это предсказание. Потом проверяем. Если результат другой, это не ошибка — это новое знание.»',
    exampleDialogue:
      'Parent: Как ты думаешь, скрепка притянется?\nChild: Да.\nParent: Проверим. Что получилось?\nChild: Притянулась.\nParent: А карандаш?\nChild: Нет.\nParent: Какой вывод?\nChild: Магнит притягивает скрепку, но не карандаш.',
    readingSupport: 'Слова «притягивает» и «не притягивает» читать медленно по частям.',
    russianSpeechGoal: 'Анита говорит: «Магнит притягивает ___. Магнит не притягивает ___.»',
    mathAnswerKey:
      'math-magnet-sort: 1) 10. 2) 2. 3) 6.\ndata-magnet-chart: 1) 8. 2) 2. 3) 4.',
    spellingAnswerKey: 'сила\nмагнит\nметалл\nопыт',
    answerKey: [
      { taskId: 'reading-words', answer: 'Попытка чтения 8 русских слов.' },
      { taskId: 'reading-sentences', answer: 'Попытка чтения 3 коротких фраз.' },
      { taskId: 'math-magnet-sort', answer: '10; 2; 6.' },
      { taskId: 'magnet-prediction', answer: 'Скорее всего скрепка и металлическая крышка; монета зависит; карандаш, ложка и бумага обычно нет.' },
      { taskId: 'science-magnet-test', answer: 'Безопасная проверка 3 предметов под присмотром родителя.' },
      { taskId: 'data-magnet-chart', answer: '8; 2; 4.' },
      { taskId: 'spelling-force', answer: 'сила, магнит, металл, опыт.' },
      { taskId: 'creative-magnet-card', answer: 'Карточка опыта с магнитом, 3 предметами и одним выводом.' },
      { taskId: 'speech-magnet-explain', answer: 'Объяснение результата по-русски.' }
    ],
    check: [
      'Анита прочитала слова и фразы.',
      'Анита решила math tasks.',
      'Анита сделала предсказание.',
      'Анита провела безопасный опыт с родителем.',
      'Анита сделала карточку и вывод.',
      'Анита объяснила результат по-русски.'
    ],
    spellingCheck: 'Проверить сила, магнит, металл, опыт.',
    readingCheck: 'Главное — попытка чтения. Слова «притягивает» и «металл» могут быть сложными.',
    practicalCheck: 'Опыт только с родителем и только с большим безопасным магнитом.',
    drawingCriteria: 'Есть магнит, 3 предмета, подписи и один вывод.',
    commonMistakes: [
      '«Магнит притягивает всё.» Правильно: «Магнит притягивает некоторые предметы.»',
      '«Если предсказание неверное, я ошиблась плохо.» Правильно: «Предсказание проверяют. Новый результат — новое знание.»',
      '«Опыт можно делать с любыми магнитами.» Правильно: «Только большой безопасный магнит с родителем.»'
    ],
    discussionPrompts: [
      'Что значит толкать?',
      'Что значит тянуть?',
      'Какие предметы притянулись?',
      'Чем предсказание отличается от результата?'
    ],
    ifEasy: 'Пусть Анита проверит 5 предметов и сделает таблицу.',
    ifDifficult: 'Проверить только 2 предмета: один притягивается, один нет.',
    rewardGuidance: 'Базовая награда — 3 нима. Бонус за аккуратную карточку опыта или ясный вывод.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Не использовать мелкие магниты. Не давать магниты маленьким детям. Не подносить магнит к телефону, компьютеру, картам или медицинским устройствам.',
    safetyNotes: 'Опыт только под присмотром родителя. Использовать только большой безопасный магнит. Не брать магнит в рот. Не использовать мелкие магниты. Не подносить магнит к телефону, компьютеру, банковским картам или медицинским устройствам.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'neat-experiment-card', label: 'Бонус за аккуратную карточку опыта', amount: 1 },
      { id: 'clear-conclusion', label: 'Бонус за ясный вывод по-русски', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'math-magnet-sort',
      'magnet-prediction',
      'science-magnet-test',
      'data-magnet-chart',
      'spelling-force',
      'creative-magnet-card',
      'speech-magnet-explain'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-3-day-4'
};

export default day3;
